// Transcrição — Whisper self-hosted (openai-whisper, ROCm RX 6800), stack /opt/stacks/whisper.
// Upload OU gravação pelo microfone → texto. Modo do painel Tecnologia, irmão do
// Sintetizador de Voz (OmniVoiceLab). API pública reusa o domínio do TTS:
// https://tts.twms.com.br/v1/audio/transcriptions (Bearer rotacionável).
import { useState, useEffect, useRef, useCallback } from 'react'
import { FiMic, FiUploadCloud, FiLoader, FiCopy, FiCheck, FiRefreshCw, FiChevronDown, FiChevronRight, FiZap, FiFileText, FiDownload, FiStopCircle, FiTrash2 } from 'react-icons/fi'
import { API_ENDPOINTS } from '../lib/api'

interface WhisperLabProps {
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>
}

// Idiomas oferecidos no seletor. 'auto' deixa o Whisper detectar; pt é o default
// do negócio (a Distribuição é PT-BR), e melhora a precisão vs deixar adivinhar.
const LANGUAGES: { code: string; label: string }[] = [
  { code: 'pt', label: 'Português' },
  { code: 'auto', label: 'Detectar' },
  { code: 'es', label: 'Espanhol' },
  { code: 'en', label: 'Inglês' },
]

export function WhisperLab({ authenticatedFetch }: WhisperLabProps) {
  const [info, setInfo] = useState<any | null>(null)
  const [infoError, setInfoError] = useState<string | null>(null)

  const [file, setFile] = useState<File | null>(null)
  const [prompt, setPrompt] = useState('')
  const [language, setLanguage] = useState('pt')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [text, setText] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [copiedText, setCopiedText] = useState(false)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [rich, setRich] = useState(false)
  const [analysis, setAnalysis] = useState<any | null>(null)

  // Gravação pelo microfone (MediaRecorder → blob → vira o file a transcrever).
  const [recording, setRecording] = useState(false)
  const [recSecs, setRecSecs] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const recTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // API pública (tts.twms.com.br)
  const [showApi, setShowApi] = useState(false)
  const [apiKey, setApiKey] = useState<string | null>(null)
  const [apiKeyError, setApiKeyError] = useState<string | null>(null)
  const [revealKey, setRevealKey] = useState(false)
  const [rotating, setRotating] = useState(false)
  const [copiedKey, setCopiedKey] = useState(false)

  const loadInfo = useCallback(async () => {
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.WHISPER_INFO)
      const data = await r.json().catch(() => ({}))
      if (!data.ok) throw new Error(data.error || 'Whisper indisponível')
      setInfo(data.info); setInfoError(null)
    } catch (e) {
      setInfoError(e instanceof Error ? e.message : 'Falha ao consultar o serviço Whisper')
    }
  }, [authenticatedFetch])

  const loadKey = useCallback(async () => {
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.WHISPER_KEY)
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setApiKey(data.token); setApiKeyError(null)
    } catch (e) {
      setApiKeyError(e instanceof Error ? e.message : 'Falha ao ler a chave')
    }
  }, [authenticatedFetch])

  useEffect(() => { loadInfo(); loadKey() }, [loadInfo, loadKey])

  // Preview de áudio do arquivo/gravação selecionado. Revoga a URL anterior.
  useEffect(() => {
    if (!file) { setAudioUrl(null); return }
    const url = URL.createObjectURL(file)
    setAudioUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  // Limpa o timer de gravação ao desmontar.
  useEffect(() => () => { if (recTimerRef.current) clearInterval(recTimerRef.current) }, [])

  const pickFile = (f: File | null) => { setFile(f); setText(null); setError(null); setElapsed(null) }

  const transcribe = async () => {
    if (!file || loading) return
    setError(null); setLoading(true); setText(null); setAnalysis(null); setElapsed(null)
    const t0 = Date.now()
    try {
      const fd = new FormData()
      fd.append('audio', file)
      if (language && language !== 'auto') fd.append('language', language)
      if (prompt.trim()) fd.append('prompt', prompt.trim())
      const url = rich ? API_ENDPOINTS.WHISPER_ANALYZE : API_ENDPOINTS.WHISPER_TRANSCRIBE
      const r = await authenticatedFetch(url, { method: 'POST', body: fd, timeout: 300000 } as RequestInit)
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      if (rich) { setAnalysis(data); setText((data.rawText || '').trim() || '(vazio)') }
      else { setText(data.text || '(vazio)') }
      setElapsed((Date.now() - t0) / 1000)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Falha na transcrição')
    } finally { setLoading(false) }
  }

  // mm:ss a partir de segundos (timestamps dos segmentos).
  const mmss = (s: number) => {
    const t = Math.max(0, Math.floor(s || 0))
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`
  }

  const startRecording = async () => {
    if (recording) return
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mr = new MediaRecorder(stream)
      chunksRef.current = []
      mr.ondataavailable = e => { if (e.data.size) chunksRef.current.push(e.data) }
      mr.onstop = () => {
        const type = mr.mimeType || 'audio/webm'
        const ext = type.includes('ogg') ? 'ogg' : type.includes('mp4') ? 'mp4' : 'webm'
        const blob = new Blob(chunksRef.current, { type })
        pickFile(new File([blob], `gravacao.${ext}`, { type }))
        stream.getTracks().forEach(t => t.stop())
      }
      mr.start()
      mediaRecorderRef.current = mr
      setRecording(true); setRecSecs(0)
      recTimerRef.current = setInterval(() => setRecSecs(s => s + 1), 1000)
    } catch (e) {
      setError('Microfone indisponível: ' + (e instanceof Error ? e.message : 'permissão negada'))
    }
  }

  const stopRecording = () => {
    if (recTimerRef.current) { clearInterval(recTimerRef.current); recTimerRef.current = null }
    mediaRecorderRef.current?.stop()
    setRecording(false)
  }

  const rotateKey = async () => {
    if (rotating) return
    if (!window.confirm('Gerar uma nova chave Bearer? A chave atual deixa de funcionar imediatamente.')) return
    setRotating(true); setApiKeyError(null)
    try {
      const r = await authenticatedFetch(API_ENDPOINTS.WHISPER_KEY_ROTATE, { method: 'POST' })
      const data = await r.json().catch(() => ({}))
      if (!r.ok || !data.ok) throw new Error(data.error || `HTTP ${r.status}`)
      setApiKey(data.token); setRevealKey(true)
    } catch (e) {
      setApiKeyError(e instanceof Error ? e.message : 'Falha ao gerar nova chave')
    } finally { setRotating(false) }
  }

  const downloadText = () => {
    if (!text) return
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = 'transcricao.txt'
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const onDrop = (e: React.DragEvent) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) pickFile(f) }

  const serviceUp = !!info && info.ok !== false
  const wordCount = text ? text.trim().split(/\s+/).filter(Boolean).length : 0

  return (
    <div className="w-full max-w-5xl mx-auto flex flex-col gap-3 p-1">
      <div className="flex items-center gap-2">
        <FiFileText className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-bold text-foreground">Transcrição — Whisper</h2>
        <span className="text-[11px] text-muted-foreground">openai-whisper · {info?.model || '…'} · {info?.device || ''} (RX6800)</span>
        <span className="ml-auto flex items-center gap-1.5">
          <span className={`w-1.5 h-1.5 rounded-full ${serviceUp ? 'bg-emerald-500' : 'bg-red-500'}`} />
          <span className="text-[11px] text-muted-foreground">{serviceUp ? (info?.loaded ? 'residente' : 'ocioso') : 'offline'}</span>
          <button onClick={loadInfo} title="Atualizar" className="p-1 rounded hover:bg-muted transition-colors"><FiRefreshCw className="w-3 h-3 text-muted-foreground" /></button>
        </span>
      </div>

      {infoError && <div className="px-2.5 py-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-700 dark:text-red-300">Serviço Whisper inacessível: {infoError}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
        {/* ── Esquerda: transcrição ── */}
        <div className="flex flex-col gap-3 min-w-0">
          <div onDrop={onDrop} onDragOver={e => { e.preventDefault(); setDragOver(true) }} onDragLeave={() => setDragOver(false)}
            className={`relative rounded-lg border-2 border-dashed p-4 flex flex-col items-center justify-center gap-2 text-center transition-colors neu-well ${dragOver ? 'border-primary bg-primary/5' : 'border-border'}`}>
            <FiUploadCloud className="w-6 h-6 text-muted-foreground" />
            <p className="text-[12px] text-foreground">{file ? <span className="font-medium">{file.name}</span> : 'Arraste um áudio aqui, escolha um arquivo ou grave'}</p>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium hover:bg-muted cursor-pointer">
                <FiUploadCloud className="w-3.5 h-3.5" /> escolher arquivo
                <input type="file" accept="audio/*,video/*" className="hidden" onChange={e => pickFile(e.target.files?.[0] || null)} />
              </label>
              {!recording ? (
                <button type="button" onClick={startRecording}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12px] font-medium hover:bg-muted">
                  <FiMic className="w-3.5 h-3.5" /> gravar
                </button>
              ) : (
                <button type="button" onClick={stopRecording}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-500 text-white px-3 py-1.5 text-[12px] font-medium hover:bg-red-600">
                  <FiStopCircle className="w-3.5 h-3.5 animate-pulse" /> parar ({recSecs}s)
                </button>
              )}
            </div>
            <p className="text-[10px] text-muted-foreground">ogg/opus (WhatsApp), mp3, wav, m4a, webm…</p>
          </div>

          {audioUrl && (
            <div className="flex items-center gap-2">
              <audio controls src={audioUrl} className="flex-1 h-9" />
              <button onClick={() => pickFile(null)} title="Remover áudio" className="p-2 rounded hover:bg-muted transition-colors"><FiTrash2 className="w-4 h-4 text-muted-foreground" /></button>
            </div>
          )}

          <div className="flex items-center gap-2">
            <select value={language} onChange={e => setLanguage(e.target.value)} title="Idioma do áudio"
              className="rounded border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary">
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            <input value={prompt} onChange={e => setPrompt(e.target.value)}
              placeholder="Prompt opcional (nomes próprios, contexto — melhora a precisão)"
              className="flex-1 min-w-0 rounded border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>

          <div className="flex items-center gap-2">
            <button onClick={transcribe} disabled={loading || !file || !serviceUp || recording}
              className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground neu-relief px-4 py-2 text-sm font-medium disabled:opacity-40">
              {loading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiFileText className="w-4 h-4" />}{loading ? (rich ? 'Analisando…' : 'Transcrevendo…') : (rich ? 'Analisar' : 'Transcrever')}
            </button>
            {elapsed != null && !loading && <span className="text-[11px] text-muted-foreground tabular-nums">em {elapsed.toFixed(1)}s</span>}
            <label className="ml-auto flex items-center gap-1.5 text-[11px] text-muted-foreground cursor-pointer" title="Transcrição + timestamps por segmento/palavra">
              <input type="checkbox" checked={rich} onChange={e => setRich(e.target.checked)} className="accent-primary" />
              análise rica
            </label>
          </div>

          {error && <div className="text-[11px] text-red-600 dark:text-red-400">{error}</div>}

          {text != null && (
            <div className="flex flex-col gap-1.5 bg-card border border-border neu-card rounded-lg p-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Transcrição</span>
                <div className="flex items-center gap-1">
                  <button onClick={downloadText} className="p-1 rounded hover:bg-muted" title="Baixar .txt"><FiDownload className="w-3.5 h-3.5" /></button>
                  <button onClick={() => { navigator.clipboard?.writeText(text); setCopiedText(true); setTimeout(() => setCopiedText(false), 1500) }}
                    className="p-1 rounded hover:bg-muted" title="Copiar">{copiedText ? <FiCheck className="w-3.5 h-3.5 text-emerald-500" /> : <FiCopy className="w-3.5 h-3.5" />}</button>
                </div>
              </div>
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{text}</p>
              <span className="text-[10px] text-muted-foreground tabular-nums">{wordCount} palavra{wordCount === 1 ? '' : 's'} · {text.length} caracteres</span>
            </div>
          )}

          {analysis && (
            <div className="flex flex-col gap-2 bg-card border border-border neu-card rounded-lg p-2.5">
              {typeof analysis.duration === 'number' && analysis.duration > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] text-muted-foreground tabular-nums">duração: {analysis.duration}s</span>
                </div>
              )}
              {Array.isArray(analysis.segments) && analysis.segments.length > 0 && (
                <div className="flex flex-col gap-1 max-h-56 overflow-y-auto">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Segmentos</span>
                  {analysis.segments.map((s: any, i: number) => (
                    <div key={i} className="flex gap-2 text-[12px]">
                      <span className="shrink-0 tabular-nums text-muted-foreground font-mono">{mmss(s.start)}</span>
                      {s.speaker && <span className="shrink-0 text-primary font-medium">{s.speaker}</span>}
                      <span className="text-foreground">{s.text}</span>
                    </div>
                  ))}
                </div>
              )}
              {(!analysis.speakers || analysis.speakers.length === 0) && (
                <span className="text-[10px] text-muted-foreground">diarização de speakers (quem falou) entra na próxima fase</span>
              )}
            </div>
          )}
        </div>

        {/* ── Direita: API pública ── */}
        <div className="flex flex-col gap-3 min-w-0">
          <div className="border border-border neu-card rounded-lg overflow-hidden">
            <button onClick={() => setShowApi(s => !s)} className="w-full flex items-center gap-1.5 px-3 py-2 text-[12px] font-semibold text-foreground hover:bg-muted/60 transition-colors">
              {showApi ? <FiChevronDown className="w-3.5 h-3.5" /> : <FiChevronRight className="w-3.5 h-3.5" />}
              <FiZap className="w-3.5 h-3.5 text-primary" /> API pública (tts.twms.com.br)
            </button>
            {showApi && (
              <div className="px-3 pb-3 flex flex-col gap-3 text-[12px] text-foreground">
                <p className="text-[11px] text-muted-foreground">
                  Transcrição via API, em <span className="font-mono">https://tts.twms.com.br/v1/audio/transcriptions</span> com TLS (mesmo domínio do TTS, roteado por caminho). Contrato <span className="font-medium text-foreground">OpenAI</span>. Autenticação por <span className="font-medium text-foreground">Bearer token</span>.
                </p>

                <div className="rounded-lg border border-border bg-card neu-card p-2.5 flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Chave Bearer</span>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setRevealKey(v => !v)} className="text-[11px] text-primary hover:underline">{revealKey ? 'ocultar' : 'mostrar'}</button>
                      <button onClick={() => { if (apiKey) { navigator.clipboard?.writeText(apiKey); setCopiedKey(true); setTimeout(() => setCopiedKey(false), 1500) } }} disabled={!apiKey} title="Copiar" className="p-1 rounded hover:bg-muted disabled:opacity-40">{copiedKey ? <FiCheck className="w-3.5 h-3.5 text-emerald-500" /> : <FiCopy className="w-3.5 h-3.5" />}</button>
                      <button onClick={rotateKey} disabled={rotating} className="inline-flex items-center gap-1 rounded-lg bg-primary text-primary-foreground neu-relief px-2.5 py-1 text-[11px] font-medium hover:opacity-90 disabled:opacity-40">
                        {rotating ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiRefreshCw className="w-3 h-3" />} Gerar nova
                      </button>
                    </div>
                  </div>
                  <code className="block w-full break-all rounded neu-pit !text-slate-200 px-2 py-1.5 text-[11px] font-mono">
                    {apiKey ? (revealKey ? apiKey : '•'.repeat(40)) : (apiKeyError ? '—' : 'carregando…')}
                  </code>
                  {apiKeyError && <span className="text-[11px] text-red-600 dark:text-red-400">{apiKeyError}</span>}
                  <span className="text-[10px] text-muted-foreground">Gerar nova invalida a anterior na hora. Guarde com cuidado.</span>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Exemplo (curl)</span>
                  <pre className="w-full overflow-x-auto rounded border border-border bg-background px-2 py-1.5 text-[10.5px] font-mono leading-relaxed text-foreground">{`curl -X POST https://tts.twms.com.br/v1/audio/transcriptions \\
  -H "Authorization: Bearer ${revealKey && apiKey ? apiKey : '<SUA_CHAVE>'}" \\
  -F "file=@audio.ogg" \\
  -F "language=pt"`}</pre>
                  <p className="text-[10px] text-muted-foreground">
                    Campos: <span className="font-mono">file</span> (obrigatório), <span className="font-mono">language</span> (pt), <span className="font-mono">prompt</span> (contexto opcional), <span className="font-mono">response_format</span> (json|text). Resposta: <span className="font-mono">{`{ "text": "…" }`}</span>.
                  </p>
                </div>

                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Modo rico — <span className="font-mono normal-case">/v1/audio/analyze</span></span>
                  <pre className="w-full overflow-x-auto rounded border border-border bg-background px-2 py-1.5 text-[10.5px] font-mono leading-relaxed text-foreground">{`curl -X POST https://tts.twms.com.br/v1/audio/analyze \\
  -H "Authorization: Bearer ${revealKey && apiKey ? apiKey : '<SUA_CHAVE>'}" \\
  -F "file=@audio.ogg" -F "language=pt"`}</pre>
                  <p className="text-[10px] text-muted-foreground">
                    Traz <span className="font-mono">rawText</span> + <span className="font-mono">segments[]</span> com timestamp por palavra. <span className="font-mono">speaker</span>/<span className="font-mono">speakers</span> = diarização (fase futura). Outros params: <span className="font-mono">prompt</span>, <span className="font-mono">word_timestamps</span> (default true).
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
